// ============================================
// Calibration Dashboard — Captain vs Dr. C Blind Comparison
// Admin page for running gold standard calibration,
// 4-dimension alignment scoring, and trend analysis.
// NEVER framer-motion. Pure CSS transitions.
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { runCalibrationSuite } from '../../services/calibration/calibrationRunner';
import { getRecentCalibrationRuns } from '../../services/firestore/calibrationRunService';
import type { CalibrationRun, CaseResult, DimensionScores } from '../../types/calibrationRun';

const DIMENSION_LABELS: { key: keyof DimensionScores; label: string; color: string }[] = [
  { key: 'supplementMatch', label: 'Supplements', color: '#E11D48' },
  { key: 'dosingAlignment', label: 'Dosing', color: '#7C3AED' },
  { key: 'labOrderMatch', label: 'Lab Orders', color: '#2563EB' },
  { key: 'monitoringMatch', label: 'Monitoring', color: '#059669' },
];

export default function CalibrationDashboard() {
  const [runs, setRuns] = useState<CalibrationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [latestResult, setLatestResult] = useState<CaseResult | null>(null);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'runs' | 'trends'>('runs');

  const fetchRuns = useCallback(async () => {
    try {
      const recent = await getRecentCalibrationRuns(20);
      setRuns(recent);
    } catch (err) {
      console.error('Failed to fetch calibration runs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const handleRun = async () => {
    setRunning(true);
    setProgress({ done: 0, total: 10 });
    setLatestResult(null);

    try {
      const result = await runCalibrationSuite((done, total, latest) => {
        setProgress({ done, total });
        setLatestResult(latest);
      });
      setRuns((prev) => [result, ...prev]);
      setExpandedRun(result.runId);
    } catch (err) {
      console.error('Calibration run failed:', err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-text-primary">Captain Calibration</h1>
          <p className="text-sm text-text-secondary mt-1">
            Blind comparison — Captain vs Dr. C's clinical decisions across 10 gold standard cases
          </p>
        </div>
        <button
          onClick={handleRun}
          disabled={running}
          className="px-5 py-2.5 rounded text-sm font-medium text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: running ? '#71717A' : '#E11D48', borderRadius: 4 }}
        >
          {running ? `Running ${progress.done}/${progress.total}...` : 'Run Calibration'}
        </button>
      </div>

      {/* Live progress */}
      {running && (
        <div className="bg-white border border-zinc-200 rounded p-4" style={{ borderRadius: 4 }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 rounded-full bg-amber-500" style={{ animation: 'pulse 1.5s infinite' }} />
            <span className="text-sm font-medium text-zinc-800">
              Running case {progress.done + 1} of {progress.total}
            </span>
          </div>
          <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${(progress.done / progress.total) * 100}%`,
                backgroundColor: '#E11D48',
                borderRadius: 4,
              }}
            />
          </div>
          {latestResult && (
            <div className="mt-3 text-sm text-zinc-600">
              Latest: <span className="font-medium">{latestResult.caseName}</span>
              {' — '}
              <span className={latestResult.flagged ? 'text-red-600 font-semibold' : 'text-emerald-600'}>
                {latestResult.flagged ? `FLAGGED (${latestResult.deviationPercent}% deviation)` : `PASS (${latestResult.recallPercent}% recall)`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-100 rounded p-1" style={{ borderRadius: 4 }}>
        <button
          onClick={() => setActiveTab('runs')}
          className="flex-1 py-2 text-sm font-medium rounded transition-colors"
          style={{
            borderRadius: 4,
            backgroundColor: activeTab === 'runs' ? '#FFFFFF' : 'transparent',
            color: activeTab === 'runs' ? '#18181B' : '#71717A',
          }}
        >
          Calibration Runs
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className="flex-1 py-2 text-sm font-medium rounded transition-colors"
          style={{
            borderRadius: 4,
            backgroundColor: activeTab === 'trends' ? '#FFFFFF' : 'transparent',
            color: activeTab === 'trends' ? '#18181B' : '#71717A',
          }}
        >
          Alignment Trends
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <p className="text-sm text-zinc-500">Loading calibration history...</p>
      ) : activeTab === 'trends' ? (
        <TrendsView runs={runs} />
      ) : runs.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded p-8 text-center" style={{ borderRadius: 4 }}>
          <p className="text-zinc-500 text-sm">No calibration runs yet. Click "Run Calibration" to start.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => (
            <RunCard
              key={run.runId}
              run={run}
              expanded={expandedRun === run.runId}
              onToggle={() => setExpandedRun(expandedRun === run.runId ? null : run.runId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Trends View ──

function TrendsView({ runs }: { runs: CalibrationRun[] }) {
  // Show only completed runs in chronological order (oldest first)
  const completedRuns = runs
    .filter((r) => r.status === 'completed' && r.dimensionAverages)
    .reverse();

  if (completedRuns.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 rounded p-8 text-center" style={{ borderRadius: 4 }}>
        <p className="text-zinc-500 text-sm">No completed runs to trend. Run calibration first.</p>
      </div>
    );
  }

  const maxRuns = Math.min(completedRuns.length, 10);
  const displayRuns = completedRuns.slice(-maxRuns);

  return (
    <div className="space-y-4">
      {/* Dimension trend bars */}
      <div className="bg-white border border-zinc-200 rounded p-5" style={{ borderRadius: 4 }}>
        <h3 className="text-sm font-semibold text-zinc-800 mb-4">4-Dimension Alignment Over Time</h3>
        <div className="space-y-6">
          {DIMENSION_LABELS.map(({ key, label, color }) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-zinc-600">{label}</span>
                <span className="text-xs font-mono" style={{ color }}>
                  {displayRuns.length > 0 ? `${displayRuns[displayRuns.length - 1].dimensionAverages?.[key] ?? 0}%` : '—'}
                </span>
              </div>
              <div className="flex items-end gap-1 h-8">
                {displayRuns.map((run, i) => {
                  const value = run.dimensionAverages?.[key] ?? 0;
                  return (
                    <div
                      key={run.runId}
                      className="flex-1 rounded-t transition-all"
                      style={{
                        height: `${Math.max(2, (value / 100) * 32)}px`,
                        backgroundColor: color,
                        opacity: 0.3 + (i / displayRuns.length) * 0.7,
                        borderRadius: '2px 2px 0 0',
                      }}
                      title={`${new Date(run.startedAt).toLocaleDateString()} — ${value}%`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overall recall/precision trend */}
      <div className="bg-white border border-zinc-200 rounded p-5" style={{ borderRadius: 4 }}>
        <h3 className="text-sm font-semibold text-zinc-800 mb-4">Overall Recall &amp; Precision</h3>
        <div className="flex items-end gap-1 h-16">
          {displayRuns.map((run) => (
            <div key={run.runId} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="w-full flex gap-0.5" style={{ height: 48 }}>
                <div
                  className="flex-1 rounded-t"
                  style={{
                    height: `${Math.max(2, (run.overallRecall / 100) * 48)}px`,
                    backgroundColor: '#E11D48',
                    borderRadius: '2px 2px 0 0',
                    marginTop: 'auto',
                  }}
                  title={`Recall: ${run.overallRecall}%`}
                />
                <div
                  className="flex-1 rounded-t"
                  style={{
                    height: `${Math.max(2, (run.overallPrecision / 100) * 48)}px`,
                    backgroundColor: '#71717A',
                    borderRadius: '2px 2px 0 0',
                    marginTop: 'auto',
                  }}
                  title={`Precision: ${run.overallPrecision}%`}
                />
              </div>
              <span className="text-[10px] text-zinc-400 whitespace-nowrap">
                {new Date(run.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E11D48' }} />
            <span className="text-xs text-zinc-500">Recall</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#71717A' }} />
            <span className="text-xs text-zinc-500">Precision</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Run Card ──

function RunCard({ run, expanded, onToggle }: {
  run: CalibrationRun;
  expanded: boolean;
  onToggle: () => void;
}) {
  const ts = new Date(run.startedAt);
  const dateStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dims = run.dimensionAverages;

  return (
    <div className="bg-white border border-zinc-200 rounded overflow-hidden" style={{ borderRadius: 4 }}>
      {/* Summary row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <StatusBadge drift={run.driftDetected} status={run.status} />
          <div>
            <p className="text-sm font-medium text-zinc-800">{dateStr} at {timeStr}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {run.passedCases}/{run.totalCases} passed · Recall {run.overallRecall}% · Precision {run.overallPrecision}%
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Dimension mini-bars */}
          {dims && (
            <div className="hidden sm:flex items-center gap-1">
              {DIMENSION_LABELS.map(({ key, color, label }) => (
                <div key={key} className="flex flex-col items-center" title={`${label}: ${dims[key]}%`}>
                  <div className="w-5 h-5 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${color}15` }}>
                    <span className="text-[9px] font-mono font-bold" style={{ color }}>
                      {dims[key]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {run.flaggedCases > 0 && (
            <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" style={{ borderRadius: 4 }}>
              {run.flaggedCases} flagged
            </span>
          )}
          <svg
            className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-zinc-100">
          {/* Dimension summary bar */}
          {dims && <DimensionSummaryBar dimensions={dims} />}

          {/* Per-case table */}
          <div className="px-5 py-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 uppercase tracking-wide">
                  <th className="pb-2 pr-3">Case</th>
                  <th className="pb-2 pr-3">Suppl</th>
                  <th className="pb-2 pr-3">Dosing</th>
                  <th className="pb-2 pr-3">Labs</th>
                  <th className="pb-2 pr-3">Monitor</th>
                  <th className="pb-2 pr-3">Safety</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {run.results.map((r) => (
                  <CaseRow key={r.caseId} result={r} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dimension Summary Bar ──

function DimensionSummaryBar({ dimensions }: { dimensions: DimensionScores }) {
  return (
    <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-100">
      <div className="grid grid-cols-4 gap-4">
        {DIMENSION_LABELS.map(({ key, label, color }) => {
          const val = dimensions[key];
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-500">{label}</span>
                <span className="text-xs font-mono font-semibold" style={{ color }}>
                  {val}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${val}%`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Case Row ──

function CaseRow({ result }: { result: CaseResult }) {
  const [showDetail, setShowDetail] = useState(false);
  const dims = result.dimensions;

  return (
    <>
      <tr
        className="border-t border-zinc-50 cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={() => setShowDetail(!showDetail)}
      >
        <td className="py-2.5 pr-3">
          <span className="font-medium text-zinc-800">{result.caseName}</span>
        </td>
        <td className="py-2.5 pr-3 font-mono text-xs">
          <ScoreCell value={dims?.supplementMatch ?? result.recallPercent} />
        </td>
        <td className="py-2.5 pr-3 font-mono text-xs">
          <ScoreCell value={dims?.dosingAlignment ?? 0} />
        </td>
        <td className="py-2.5 pr-3 font-mono text-xs">
          <ScoreCell value={dims?.labOrderMatch ?? 0} />
        </td>
        <td className="py-2.5 pr-3 font-mono text-xs">
          <ScoreCell value={dims?.monitoringMatch ?? 0} />
        </td>
        <td className="py-2.5 pr-3">
          {result.forbiddenViolations.length > 0 ? (
            <span className="text-xs font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded" style={{ borderRadius: 4 }}>VIOLATION</span>
          ) : (
            <span className="text-xs text-emerald-600">OK</span>
          )}
        </td>
        <td className="py-2.5">
          {result.flagged ? (
            <span className="text-xs font-semibold text-red-600">FLAGGED</span>
          ) : (
            <span className="text-xs text-emerald-600">PASS</span>
          )}
        </td>
      </tr>
      {showDetail && (
        <tr>
          <td colSpan={7} className="px-4 py-3 bg-zinc-50">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <p className="font-semibold text-zinc-600 mb-1">Dr. C's Expected Products</p>
                <ul className="space-y-0.5">
                  {result.expectedProducts.length > 0 ? result.expectedProducts.map((p) => (
                    <li key={p} className={`font-mono ${result.matchedProducts.includes(p) ? 'text-emerald-700' : 'text-red-600'}`}>
                      {result.matchedProducts.includes(p) ? '\u2713' : '\u2717'} {p}
                    </li>
                  )) : <li className="text-zinc-400 italic">None (negative control)</li>}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-zinc-600 mb-1">Captain's Actual Products</p>
                <ul className="space-y-0.5">
                  {result.actualProducts.length > 0 ? result.actualProducts.map((p) => (
                    <li key={p} className={`font-mono ${result.expectedProducts.includes(p) ? 'text-emerald-700' : 'text-amber-600'}`}>
                      {result.expectedProducts.includes(p) ? '\u2713' : '?'} {p}
                    </li>
                  )) : <li className="text-zinc-400 italic">None returned</li>}
                </ul>
              </div>
              {/* Dosing comparison */}
              {result.actualDosing && result.actualDosing.length > 0 && (
                <div className="col-span-2">
                  <p className="font-semibold text-zinc-600 mb-1">Dosing Comparison</p>
                  <div className="space-y-1">
                    {result.actualDosing.map((d) => (
                      <div key={d.productId} className="flex gap-2">
                        <span className="font-mono text-zinc-500 w-48 shrink-0">{d.productId}</span>
                        <span className="text-zinc-700">{d.dosage}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Lab orders */}
              {result.expectedLabOrders && result.expectedLabOrders.length > 0 && (
                <div>
                  <p className="font-semibold text-zinc-600 mb-1">Dr. C's Lab Orders</p>
                  <ul className="space-y-0.5">
                    {result.expectedLabOrders.map((l) => (
                      <li key={l} className="text-zinc-700">{l}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Monitoring */}
              {result.expectedMonitoring && (
                <div>
                  <p className="font-semibold text-zinc-600 mb-1">Dr. C's Monitoring Plan</p>
                  <p className="text-zinc-700">{result.expectedMonitoring}</p>
                </div>
              )}
              {result.forbiddenViolations.length > 0 && (
                <div className="col-span-2">
                  <p className="font-semibold text-red-700 mb-1">Safety Violations</p>
                  <ul className="space-y-0.5">
                    {result.forbiddenViolations.map((p) => (
                      <li key={p} className="font-mono text-red-700">{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="col-span-2">
                <p className="font-semibold text-zinc-600 mb-1">Protocol Summary</p>
                <p className="text-zinc-700">{result.protocolSummary}</p>
                <p className="text-zinc-400 mt-1">{result.executionTimeMs}ms</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ScoreCell({ value }: { value: number }) {
  const color = value >= 90 ? 'text-emerald-600' : value >= 70 ? 'text-amber-600' : 'text-red-600';
  return <span className={color}>{value}%</span>;
}

function StatusBadge({ drift, status }: { drift: boolean; status: string }) {
  if (status === 'running') {
    return <div className="w-3 h-3 rounded-full bg-amber-400" style={{ animation: 'pulse 1.5s infinite' }} />;
  }
  if (drift) {
    return <div className="w-3 h-3 rounded-full bg-red-500" />;
  }
  return <div className="w-3 h-3 rounded-full bg-emerald-500" />;
}
